import React, { useState, useEffect } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import {
  Plus, Search, Filter, FileText, Clock,
  User, Wrench, Eye, Edit, ArrowRight,
  CheckCircle, XCircle, Circle, Archive, Download
} from 'lucide-react'
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query'

import Button from '../components/ui/Button'
import Input from '../components/ui/Input'
import Select from '../components/ui/Select'
import Modal from '../components/Modal'
import Pagination from '../components/ui/Pagination'

import ordensServicoService, {
  OrdemServicoFilters,
  getPrioridadeConfig,
  formatValor,
  prioridades
} from '../services/ordensServico'
import apiService from '../services/api'
import { OrdemServico, EstadoOS, Cliente, Equipamento } from '../types'
import OrdemServicoForm from '../components/ordensServico/OrdemServicoForm'
import OrdemServicoDetails from '../components/ordensServico/OrdemServicoDetails'
import ClienteSelectionModal from '../components/OrdemServico/ClienteSelectionModal'
import EquipamentoSelectionModal from '../components/OrdemServico/EquipamentoSelectionModal'
import { useToast } from '../components/Toast'

const OrdensServicoPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { success, error } = useToast()

  // Estados locais
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showDetailsModal, setShowDetailsModal] = useState(false)
  const [selectedOS, setSelectedOS] = useState<OrdemServico | null>(null)
  const [showFilters, setShowFilters] = useState(false)

  // Estados para o fluxo de criação em 3 passos
  const [showClienteModal, setShowClienteModal] = useState(false)
  const [showEquipamentoModal, setShowEquipamentoModal] = useState(false)
  const [selectedCliente, setSelectedCliente] = useState<Cliente | null>(null)
  const [selectedEquipamento, setSelectedEquipamento] = useState<Equipamento | null>(null)

  // Filtros
  const [filters, setFilters] = useState<OrdemServicoFilters>({
    page: parseInt(searchParams.get('page') || '1'),
    limit: parseInt(searchParams.get('limit') || '25'),
    search: searchParams.get('search') || '',
    estadoId: searchParams.get('estadoId') ? parseInt(searchParams.get('estadoId')!) : undefined,
    prioridade: (searchParams.get('prioridade') as any) || undefined,
    usuarioResponsavelId: searchParams.get('usuarioResponsavelId') ? parseInt(searchParams.get('usuarioResponsavelId')!) : undefined,
    arquivada: false // Mostrar apenas OS ativas (não arquivadas)
  })

  // Queries
  const { data: ordensData, isLoading, refetch } = useQuery({
    queryKey: ['ordens-servico', filters],
    queryFn: () => ordensServicoService.list(filters)
  })

  const { data: estados } = useQuery({
    queryKey: ['estados-os'],
    queryFn: () => ordensServicoService.getEstados()
  })

  // Mutation para alterar estado
  const changeStateMutation = useMutation({
    mutationFn: ({ osId, novoEstadoId }: { osId: number, novoEstadoId: number }) => {
      console.log('Alterando estado na listagem:', { osId, novoEstadoId })
      return ordensServicoService.changeState(osId, novoEstadoId)
    },
    onSuccess: () => {
      success('Estado alterado com sucesso')
      refetch()
    },
    onError: (err: any) => {
      console.error('Erro ao alterar estado na listagem:', err)
      const message = err.response?.data?.message || err.message || 'Erro desconhecido'
      error('Erro ao alterar estado', message)
    }
  })

  // Efeitos
  useEffect(() => {
    const params = new URLSearchParams()
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== '' && value !== null) {
        params.set(key, String(value))
      }
    })
    setSearchParams(params)
  }, [filters, setSearchParams])

  // Handlers
  const handleSearch = (search: string) => {
    setFilters(prev => ({ ...prev, search, page: 1 }))
  }

  const handleFilterChange = (key: keyof OrdemServicoFilters, value: any) => {
    setFilters(prev => ({ ...prev, [key]: value, page: 1 }))
  }

  const handlePageChange = (page: number) => {
    setFilters(prev => ({ ...prev, page }))
  }

  const handleCreate = () => {
    // Iniciar o fluxo de 3 passos
    setShowClienteModal(true)
  }

  const handleClienteSelect = (cliente: Cliente) => {
    setSelectedCliente(cliente)
    setShowClienteModal(false)
    setShowEquipamentoModal(true)
  }

  const handleEquipamentoSelect = (equipamento: Equipamento) => {
    setSelectedEquipamento(equipamento)
    setShowEquipamentoModal(false)
    setShowCreateModal(true)
  }

  const handleEdit = (os: OrdemServico) => {
    setSelectedOS(os)
    setShowEditModal(true)
  }

  const handleView = (os: OrdemServico) => {
    setSelectedOS(os)
    setShowDetailsModal(true)
  }

  const handleFormSuccess = (ordemServico?: OrdemServico) => {
    refetch()

    if (ordemServico && !showEditModal) {
      // Se foi uma criação (não edição), redirecionar para a página de sucesso
      navigate(`/ordens-servico/${ordemServico.id}/success`)
      setShowCreateModal(false)
    } else {
      // Se foi uma edição, apenas fechar o modal
      setShowEditModal(false)
      setShowCreateModal(false)
    }

    // Limpar todos os estados do fluxo de criação
    setSelectedOS(null)
    setSelectedCliente(null)
    setSelectedEquipamento(null)
  }

  const handleStateChange = (osId: number, novoEstadoId: string) => {
    const estadoId = parseInt(novoEstadoId)
    if (isNaN(estadoId)) return
    
    changeStateMutation.mutate({ osId, novoEstadoId: estadoId })
  }

  const clearFilters = () => {
    setFilters({
      page: 1,
      limit: 25,
      search: '',
      estadoId: undefined,
      prioridade: undefined,
      usuarioResponsavelId: undefined,
      arquivada: false // Manter apenas OS ativas
    })
  }

  // Ícone do estado
  const getStateIcon = (estado: EstadoOS) => {
    const name = estado.nome.toLowerCase()
    if (name.includes('conclu') || name.includes('entregue')) {
      return <CheckCircle className="h-4 w-4 text-green-500" />
    }
    if (name.includes('cancel') || name.includes('recus')) {
      return <XCircle className="h-4 w-4 text-red-500" />
    }
    if (name.includes('anda') || name.includes('repar')) {
      return <Wrench className="h-4 w-4 text-blue-500" />
    }
    if (name.includes('espera') || name.includes('pend')) {
      return <Clock className="h-4 w-4 text-yellow-500" />
    }
    return <Circle className="h-4 w-4 text-gray-500" />
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Ordens de Serviço</h1>
          <p className="text-gray-600">
            {ordensData?.pagination?.totalItems || 0} ordens de serviço
          </p>
        </div>
        <div className="flex flex-wrap gap-2 sm:gap-3 w-full sm:w-auto">
          <Button
            variant="outline"
            onClick={() => navigate('/ordens-servico/arquivo')}
            leftIcon={<Archive className="h-4 w-4" />}
          >
            Arquivo
          </Button>
          <Button
            variant="outline"
            onClick={() => setShowFilters(!showFilters)}
            leftIcon={<Filter className="h-4 w-4" />}
          >
            Filtros
          </Button>
          <Button
            onClick={handleCreate}
            leftIcon={<Plus className="h-4 w-4" />}
          >
            Nova Ordem de Serviço
          </Button>
        </div>
      </div>

      {/* Barra de pesquisa */}
      <div className="flex gap-4">
        <div className="flex-1">
          <Input
            placeholder="Buscar por número, cliente, equipamento..."
            value={filters.search}
            onChange={(e) => handleSearch(e.target.value)}
            leftIcon={<Search className="h-4 w-4" />}
          />
        </div>
      </div>

      {/* Filtros avançados */}
      {showFilters && (
        <div className="bg-gray-50 p-4 rounded-lg grid grid-cols-1 md:grid-cols-4 gap-4">
          <Select
            label="Estado"
            value={filters.estadoId || ''}
            onChange={(e) => handleFilterChange('estadoId', e.target.value ? parseInt(e.target.value) : undefined)}
            options={[
              { value: '', label: 'Todos os estados' },
              ...(estados?.filter((estado: any) => {
                const nome = estado.nome.toLowerCase()
                return !estado.estadoFinal &&
                       !nome.includes('entregue') &&
                       !nome.includes('cancelad') &&
                       !nome.includes('fechad') &&
                       !nome.includes('concluíd') &&
                       !nome.includes('finaliz')
              }).map((estado: any) => ({
                value: estado.id.toString(),
                label: estado.nome
              })) || [])
            ]}
          />
          
          <Select
            label="Prioridade"
            value={filters.prioridade || ''}
            onChange={(value) => handleFilterChange('prioridade', value || undefined)}
            options={[
              { value: '', label: 'Todas as prioridades' },
              ...prioridades.map(prioridade => ({
                value: prioridade.value,
                label: prioridade.label
              }))
            ]}
          />

          <div className="md:col-span-2 flex justify-end">
            <Button
              variant="outline"
              onClick={clearFilters}
            >
              Limpar Filtros
            </Button>
          </div>
        </div>
      )}

      {/* Lista de ordens de serviço */}
      <div className="bg-white shadow-sm rounded-lg overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
            <p className="mt-2 text-gray-600">Carregando ordens de serviço...</p>
          </div>
        ) : ordensData?.ordensServico?.length === 0 ? (
          <div className="p-8 text-center">
            <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900">Nenhuma ordem de serviço encontrada</h3>
            <p className="text-gray-600">
              {Object.keys(filters).some(key => filters[key as keyof OrdemServicoFilters] && key !== 'page' && key !== 'limit')
                ? 'Tente ajustar os filtros ou'
                : 'Comece por'
              } criar uma nova ordem de serviço.
            </p>
            <Button
              className="mt-4"
              onClick={handleCreate}
              leftIcon={<Plus className="h-4 w-4" />}
            >
              Nova Ordem de Serviço
            </Button>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto -mx-4 sm:mx-0 px-4 sm:px-0">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Ordem / Cliente
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Equipamento
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Estado
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Prioridade
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Técnico
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Valor
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {ordensData?.ordensServico?.map((os) => {
                    const prioridadeConfig = getPrioridadeConfig(os.prioridade)
                    
                    return (
                      <tr key={os.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {os.numero}
                            </div>
                            <div className="text-sm text-gray-500">
                              {os.cliente.nome}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {os.equipamento.modelo.marca.nome} {os.equipamento.modelo.nome}
                          </div>
                          <div className="text-sm text-gray-500">
                            {os.equipamento.modelo.tipoEquipamento.nome}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            {getStateIcon(os.estado)}
                            <select
                              value={os.estadoId}
                              onChange={(e) => handleStateChange(os.id, e.target.value)}
                              disabled={changeStateMutation.isPending}
                              className="text-sm border border-gray-200 bg-white hover:bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 rounded p-1 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                              style={{ minWidth: '140px' }}
                            >
                              {estados?.map((estado) => (
                                <option key={estado.id} value={estado.id}>
                                  {estado.nome}
                                </option>
                              ))}
                            </select>
                            {changeStateMutation.isPending && (
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 ml-1"></div>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 text-sm font-medium rounded-full ${
                            prioridadeConfig.color === 'green' ? 'bg-green-100 text-green-800' :
                            prioridadeConfig.color === 'yellow' ? 'bg-yellow-100 text-yellow-800' :
                            prioridadeConfig.color === 'red' ? 'bg-red-100 text-red-800' :
                            prioridadeConfig.color === 'blue' ? 'bg-blue-100 text-blue-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {prioridadeConfig.label}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <User className="h-4 w-4 text-gray-400" />
                            <span className="text-sm text-gray-900">
                              {os.usuarioResponsavel?.nome || 'Não atribuído'}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {formatValor(os.valorTotalComIva)}
                          </div>
                          {(() => {
                            const estadoNome = os.estado.nome.toLowerCase()
                            // Mostrar "Aguarda aprovação" apenas se o estado indicar orçamento pendente
                            if (estadoNome.includes('orçament') && !estadoNome.includes('aprovad') && os.valorOrcamento > 0) {
                              return (
                                <div className="text-xs text-yellow-600">
                                  Aguarda aprovação
                                </div>
                              )
                            }
                            // Mostrar status baseado no estado
                            if (estadoNome.includes('concluí') || estadoNome.includes('entreg') || estadoNome.includes('finaliz')) {
                              return (
                                <div className="text-xs text-green-600">
                                  Concluído
                                </div>
                              )
                            }
                            if (estadoNome.includes('andament') || estadoNome.includes('progress') || estadoNome.includes('reparaç')) {
                              return (
                                <div className="text-xs text-blue-600">
                                  Em andamento
                                </div>
                              )
                            }
                            if (estadoNome.includes('aguard') || estadoNome.includes('pendente')) {
                              return (
                                <div className="text-xs text-orange-600">
                                  Pendente
                                </div>
                              )
                            }
                            return null
                          })()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleView(os)}
                              title="Ver detalhes"
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEdit(os)}
                              title="Editar"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={async () => {
                                try {
                                  const estadoNome = os.estado.nome.toLowerCase()
                                  const isEstadoFinal = os.estado.estadoFinal ||
                                    estadoNome.includes('entregue') ||
                                    estadoNome.includes('cancelad') ||
                                    estadoNome.includes('fechad') ||
                                    estadoNome.includes('concluíd') ||
                                    estadoNome.includes('finaliz')

                                  const pdfType = isEstadoFinal ? 'fechamento' : 'ordem'
                                  await apiService.generateOrdemServicoPdf(os.id, pdfType)
                                  success(`PDF de ${pdfType === 'ordem' ? 'abertura' : 'fechamento'} gerado com sucesso`)
                                } catch (err: any) {
                                  error('Erro ao gerar PDF', err.message)
                                }
                              }}
                              title="Gerar PDF"
                            >
                              <Download className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>

            {/* Paginação */}
            {ordensData && ordensData.pagination && (
              <Pagination
                pagination={ordensData.pagination}
                onPageChange={handlePageChange}
              />
            )}
          </>
        )}
      </div>

      {/* Modais */}
      {/* Modal de seleção de cliente (Passo 1) */}
      <ClienteSelectionModal
        isOpen={showClienteModal}
        onClose={() => {
          setShowClienteModal(false)
          setSelectedCliente(null)
        }}
        onSelectCliente={handleClienteSelect}
      />

      {/* Modal de seleção de equipamento (Passo 2) */}
      {selectedCliente && (
        <EquipamentoSelectionModal
          isOpen={showEquipamentoModal}
          onClose={() => {
            setShowEquipamentoModal(false)
            setSelectedEquipamento(null)
          }}
          cliente={selectedCliente}
          onSelectEquipamento={handleEquipamentoSelect}
        />
      )}

      {/* Modal de criação de OS (Passo 3) */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => {
          setShowCreateModal(false)
          setSelectedCliente(null)
          setSelectedEquipamento(null)
        }}
        title="Nova Ordem de Serviço"
        size="xl"
      >
        <OrdemServicoForm
          clienteId={selectedCliente?.id}
          equipamentoId={selectedEquipamento?.id}
          onSuccess={handleFormSuccess}
          onCancel={() => {
            setShowCreateModal(false)
            setSelectedCliente(null)
            setSelectedEquipamento(null)
          }}
        />
      </Modal>

      <Modal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        title="Editar Ordem de Serviço"
        size="xl"
      >
        {selectedOS && (
          <OrdemServicoForm
            ordemServico={selectedOS}
            onSuccess={handleFormSuccess}
            onCancel={() => setShowEditModal(false)}
          />
        )}
      </Modal>

      <Modal
        isOpen={showDetailsModal}
        onClose={() => setShowDetailsModal(false)}
        title="Detalhes da Ordem de Serviço"
        size="xl"
      >
        {selectedOS && (
          <OrdemServicoDetails
            ordemServico={selectedOS}
            onEdit={() => {
              setShowDetailsModal(false)
              handleEdit(selectedOS)
            }}
            onClose={() => setShowDetailsModal(false)}
          />
        )}
      </Modal>
    </div>
  )
}

export default OrdensServicoPage